import * as React from 'react';

import type { ContentKitContextDocument } from '@gitbook/api-client';
import type { RawEditAnalyticsEvent } from '@gitbook/app-analytics/common';
import { type ContentKitRemote, createContentKitRemote } from '@gitbook/contentkit-react';
import { createSectionIDs, type SectionIDs } from '@gitbook/doc-core';
import type { ButtonProps } from '@gitbook/spine-react';

/**
 * Components that are passed to the gutter component.
 */
export interface GutterComponentProps {
    /** Key of the node for which the gutter is being rendered */
    nodeKey: string;

    /** True of the block is hovered */
    visible: boolean;

    /** When true, keyboard shortcuts can be binded to the node related to the gutter */
    shortcutsActive: boolean;

    /** Size of the button to display in the gutter */
    buttonSize: ButtonProps['size'];

    /** True when the editor is editable */
    readOnly: boolean;

    /** DOM properties to pass to the underlying element */
    className?: string;
    style?: React.CSSProperties;
}

/**
 * A component that can be passed to the Editor and will be rendered in the right-hand gutter.
 */
export type RightGutterComponentType = React.FunctionComponent<GutterComponentProps>;

/**
 * Props for the block highlight component.
 */
export interface BlockHighlightProps {
    /** Key of the node to highlight */
    nodeKey: string;
}

export interface BlockConfiguration {
    /**
     * Whether the content editor should support images.
     * If false, the editor will not allow inserting images (but will render them as read-only).
     */
    withImages?: boolean;

    /**
     * Whether the content editor should support file blocks.
     * If undefined, the editor will not allow inserting file blocks (but will render them as read-only).
     */
    withFiles?: boolean;

    /**
     * Whether the content editor should support API blocks.
     * If undefined, the editor will not allow inserting API blocks (but will render them as read-only).
     */
    withAPIs?: boolean;

    /**
     * Whether the content editor has capabilities to manage reusable content.
     */
    withReusableContent?: boolean;

    /**
     * Whether the content editor should support drawing blocks.
     * If undefined, the editor will not allow inserting drawing blocks (but will render them as read-only).
     */
    withDrawings?: boolean;

    /**
     * Whether the content editor should support AI completions.
     */
    withCompletions?: boolean;
}

/**
 * Context when copy/pasting content from and to the Editor.
 *
 * Technically, as we're in ContentEditor, this shouldn't know about orgs, spaces or anything.
 * But our ContentEditor is always used in the context of an organization and a space, and defining it
 * here gives us much better typing.
 */
export interface ContentEditorCopyPasteContext {
    organization: string;
    space: string;
}

/**
 * Type for the internal value of the ContentEditorContext context.
 */
export interface ContentEditorContextValue {
    /** Configuration for certain block types in the editor. */
    blocks: BlockConfiguration;

    /** Configuration for copy/pasting. */
    copyPaste: {
        /**
         * Return the current context of the Editor.
         */
        getContext: () => ContentEditorCopyPasteContext;

        /**
         * Parse a serialized context from the clipboard data. Throw an exception if the data is invalid.
         */
        parseContext: (data: unknown) => ContentEditorCopyPasteContext;
    };

    /** Callback to track analytics events */
    logAnalyticsEvent: (event: RawEditAnalyticsEvent) => void;

    /**
     * A component that can be passed to the Editor and will be rendered in the right-hand gutter.
     */
    rightGutterComponent?: RightGutterComponentType;

    /**
     * A component that can be used to highlight blocks in the editor.
     */
    blockHighlightComponent?: React.ComponentType<BlockHighlightProps>;

    /**
     * A function that will be called by the ContentEditor when a block is hovered.
     * If null is passed, it means that no block is hovered.
     */
    onBlockHover?: (nodeKey: string | null) => void;

    /**
     * Context to be used to while rendering integrations blocks.
     */
    integrationBlocksContext: ContentKitContextDocument | null;

    /**
     * Interface to fetch remote content for integrations.
     */
    integrationBlocksRemote: ContentKitRemote;
}

export const ContentEditorDefaultContext: ContentEditorContextValue = {
    copyPaste: {
        getContext: () => {
            throw new Error('not implemented');
        },
        parseContext: () => {
            throw new Error('not implemented');
        },
    },
    blocks: {},
    logAnalyticsEvent: () => {},
    integrationBlocksContext: null,
    integrationBlocksRemote: createContentKitRemote(() => {
        throw new Error('not implemented');
    }),
};

/**
 * Global context of the editor.
 */
export const ContentEditorContext = React.createContext<ContentEditorContextValue>(
    ContentEditorDefaultContext
);

/**
 * Context to store the section IDs in the document.
 */
export const EditorSectionIDsContext = React.createContext<{
    sections: SectionIDs;
    selected: string | null;
}>({
    sections: createSectionIDs(),
    selected: null,
});

export type BlockVariant = 'root' | 'caption' | 'quote' | 'hint' | 'expandableTitle';

/**
 * Context to access the current variant of blocks.
 * When rendering some fragments, we want to change how their children look like.
 * The parent of the fragment renderer can set this context to do just that.
 */
export const BlockVariantContext = React.createContext<BlockVariant>('root');
